<script setup lang="ts">
import { RestaurantAreaType } from '~/lib/interfaces/restaurant'
import { Category } from '~/lib/services/store/catalog/catalog.dtos'
import { RestaurantInfo } from '~/lib/services/store/restaurant/restaurant.dto'

const props = withDefaults(
  defineProps<{
    categories?: Category[]
    currentCategoryId?: string
    restaurantInfo?: RestaurantInfo
    area?: RestaurantAreaType
  }>(),
  {
    categories: undefined,
    currentCategoryId: undefined,
    restaurantInfo: undefined,
    area: undefined,
  }
)
const isMobile = inject<Ref<boolean>>('isMobile')
const appStore = useAppStore()
const { getCategoryRoute, staticCategoryRoute } = useMcdRoutes()
const swiper = ref()
const { trackClickCategory } = useGTM()

const centerInsufficientSlides = computed(() => {
  if (isMobile && !isMobile.value) return true
  return false
})

const setControlledSwiper = (swiperInstance: any) => {
  swiper.value = swiperInstance

  // Navigate to slide
  if (props.currentCategoryId) {
    if (props.categories) {
      // on ecommerce data
      const indexOfSlide = props.categories?.findIndex(cat => cat.idRfm === props.currentCategoryId)
      if (indexOfSlide !== -1) swiper?.value.slideTo(indexOfSlide)
    } else {
      // on static categories
      const indexOfSlide = appStore.staticCategories?.findIndex(
        cat => cat._id === props.currentCategoryId
      )
      if (indexOfSlide !== -1) swiper?.value.slideTo(indexOfSlide)
    }
  }
}

const ecommerceCategoryImage = (cat: Category) => {
  const activeFlagCategoriesImages = !!appStore.ecommerceConfig?.activeCategoriesImages
  return activeFlagCategoriesImages ? cat.imageUrl : cat.products[0]?.imageUrl
}

/* WIP
  - TODO: show more link to open menu in a modal?? - TBD, for now is ok
*/

onErrorCaptured(err => {
  // we capture the error to avoid the app to crash
  // eslint-disable-next-line no-console
  console.log('onErrorCaptured:', err)
  return false
})
</script>

<template>
  <section v-if="appStore.initialFetchLoaded" class="mx-auto my-4 w-full md:w-[85%]">
    <!-- Ecommerce categories -->
    <template v-if="restaurantInfo && categories?.length">
      <Swiper
        :slides-per-view="'auto'"
        :center-insufficient-slides="centerInsufficientSlides"
        @swiper="setControlledSwiper"
      >
        <SwiperSlide
          v-for="(cat, index) in categories.filter(c => c.active)"
          :key="`h_ecommerce_menu_category_${cat.idRfm}`"
          @click="trackClickCategory(cat.title, index)"
        >
          <NuxtLink
            :to="
              getCategoryRoute({
                restaurantInfo: restaurantInfo,
                categoryTitle: cat.title,
                routeArea: area,
              })
            "
            class="hover-scale block"
            :class="{ 'block border-b-4 border-b-mcd-main': cat.idRfm === currentCategoryId }"
          >
            <div class="md: flex w-[5.5rem] flex-col items-center p-2 md:w-full md:max-w-[6.5rem]">
              <ImageResolver
                class="my-2 h-auto w-[42px]"
                :url="ecommerceCategoryImage(cat)"
                :alt="`${cat.title} image`"
                loading="lazy"
              />
              <p class="min-h-[32px] text-center text-xs md:text-sm">{{ cat.title }}</p>
            </div>
          </NuxtLink>
        </SwiperSlide>
      </Swiper>
    </template>
    <!-- Static MDW categories -->
    <template v-else-if="appStore.staticCategories">
      <Swiper :slides-per-view="'auto'" :center-insufficient-slides="centerInsufficientSlides">
        <SwiperSlide
          v-for="(cat, index) in appStore.staticCategories"
          :key="`h_static_menu_category_${cat._id}`"
          @click="trackClickCategory(cat.name, index)"
        >
          <NuxtLink
            :to="staticCategoryRoute(cat.name)"
            class="hover-scale block"
            :class="{ 'block border-b-4 border-b-mcd-main': cat._id === currentCategoryId }"
          >
            <div class="md: flex w-[5.5rem] flex-col items-center p-2 md:w-full md:max-w-[6.5rem]">
              <ImageResolver
                class="my-2 h-auto w-[42px]"
                loading="lazy"
                :url="cat.image"
                :alt="`${cat.name} image`"
              />
              <p class="min-h-[32px] text-center text-xs md:text-sm">{{ cat.name }}</p>
            </div>
          </NuxtLink>
        </SwiperSlide>
      </Swiper>
    </template>
  </section>
</template>

<style scoped>
.swiper-slide {
  @apply mr-2 w-fit md:mr-6;
}
.swiper {
  @apply pl-4 md:pl-0;
}
</style>
